
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LogOut, Plus } from 'lucide-react';

interface Task {
  id: string;
  section: string;
  step_no: number;
  task: string;
  notes: string | null;
  done: boolean;
  updated_at: string;
}

interface LogEntry {
  id: string;
  task_id: string | null;
  task_text: string | null;
  action: string;
  user_email: string | null;
  at: string;
}

const ArayoProgress = () => {
  const { user, signOut, loading } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sectionFilter, setSectionFilter] = useState('ALL');
  const [loadingTasks, setLoadingTasks] = useState(true);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!loading && !user) {
      navigate('/login');
      return;
    }

    if (user) {
      loadTasks();
      loadLogs();
      
      // Set up realtime subscription
      const channel = supabase
        .channel('schema-db-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'checklist_tasks'
          },
          () => {
            loadTasks();
          }
        )
        .subscribe();

      // Cleanup function
      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user, loading, navigate]);

  const loadTasks = async () => {
    try {
      const { data, error } = await supabase
        .from('checklist_tasks')
        .select('*')
        .order('section', { ascending: true })
        .order('step_no', { ascending: true });

      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const loadLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('checklist_logs')
        .select('*')
        .order('at', { ascending: false });

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  const updateTaskDone = async (taskId: string, done: boolean, taskText: string) => {
    try {
      // Update task
      const { error: updateError } = await supabase
        .from('checklist_tasks')
        .update({ done, updated_at: new Date().toISOString() })
        .eq('id', taskId);

      if (updateError) throw updateError;

      // Log the action
      const { error: logError } = await supabase
        .from('checklist_logs')
        .insert({
          task_id: taskId,
          task_text: taskText,
          action: done ? 'completed' : 'unchecked',
          user_email: user?.email || null
        });

      if (logError) throw logError;

      loadLogs();
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const updateTaskNotes = async (taskId: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('checklist_tasks')
        .update({ notes, updated_at: new Date().toISOString() })
        .eq('id', taskId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating notes:', error);
    }
  };

  const addManualNote = async () => {
    const noteText = prompt('Enter your note:');
    if (!noteText?.trim()) return;

    try {
      const { error } = await supabase
        .from('checklist_logs')
        .insert({
          task_id: null,
          task_text: noteText,
          action: 'note',
          user_email: user?.email || null
        });

      if (error) throw error;
      loadLogs();
    } catch (error) {
      console.error('Error adding note:', error);
    }
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const matchesSearch = searchTerm === '' || 
        task.task.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.notes && task.notes.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesSection = sectionFilter === 'ALL' || task.section === sectionFilter;
      
      return matchesSearch && matchesSection;
    });
  }, [tasks, searchTerm, sectionFilter]);

  const sections = useMemo(() => {
    const uniqueSections = [...new Set(tasks.map(task => task.section))].sort();
    return uniqueSections;
  }, [tasks]);

  const progressStats = useMemo(() => {
    const total = 153; // Always 153 tasks total
    const done = tasks.filter(task => task.done).length; // Count all completed tasks
    const percent = Math.round((done / total) * 100);
    return { total, done, percent };
  }, [tasks]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  if (loading || loadingTasks) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">ARAYO.AI Progress</h1>
          <Button onClick={handleSignOut} variant="outline" className="gap-2">
            <LogOut className="w-4 h-4" />
            Sign Out
          </Button>
        </div>

        {/* Progress Bar */}
        <Card>
          <CardHeader>
            <CardTitle>Progress Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Completed: {progressStats.done} / {progressStats.total}</span>
                <span>{progressStats.percent}%</span>
              </div>
              <Progress value={progressStats.percent} className="h-3" />
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4 flex-wrap">
              <div className="flex-1 min-w-64">
                <Input
                  placeholder="Search tasks or notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={sectionFilter} onValueChange={setSectionFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Sections</SelectItem>
                  {sections.map(section => (
                    <SelectItem key={section} value={section}>{section}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tasks Table */}
        <Card>
          <CardHeader>
            <CardTitle>Checklist Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Section</TableHead>
                    <TableHead className="w-20">Step</TableHead>
                    <TableHead>Task</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="w-20">Done</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTasks.map((task) => (
                    <TableRow key={task.id}>
                      <TableCell className="font-medium">{task.section}</TableCell>
                      <TableCell>{task.step_no}</TableCell>
                      <TableCell>{task.task}</TableCell>
                      <TableCell>
                        <Textarea
                          value={task.notes || ''}
                          onChange={(e) => {
                            const newTasks = tasks.map(t => 
                              t.id === task.id ? { ...t, notes: e.target.value } : t
                            );
                            setTasks(newTasks);
                          }}
                          onBlur={(e) => updateTaskNotes(task.id, e.target.value)}
                          placeholder="Add notes..."
                          className="min-h-[60px] resize-none"
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          checked={task.done}
                          onCheckedChange={(checked) => 
                            updateTaskDone(task.id, !!checked, task.task)
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Log Panel */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Activity Log</CardTitle>
            <Button onClick={addManualNote} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" />
              Add Note
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {logs.map((log) => (
                <div key={log.id} className="flex justify-between items-start p-3 bg-muted/50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium capitalize">{log.action}</div>
                    {log.task_text && (
                      <div className="text-sm text-muted-foreground mt-1">{log.task_text}</div>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground text-right ml-4">
                    <div>{new Date(log.at).toLocaleString()}</div>
                    {log.user_email && (
                      <div className="mt-1">{log.user_email}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ArayoProgress;
